import React, { useEffect, useState } from "react";
import { Typography, TextField, Button } from "@material-ui/core";
import cx from "classnames";

import classes from "./YesDelete.module.scss";

type DeletionType = "user" | "department";
interface DeletionData {
  teamMembers?: number;
  invitations?: number;
  clients?: number;
  department?: {
    name: string;
  };
}

interface YesDeleteProps {
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
  deletionData: DeletionData;
  deletionType: DeletionType;
  verifyPhrase: string;
  classname?: string;
}

const YesDelete: React.FC<YesDeleteProps> = ({
  onConfirmDelete,
  onCancelDelete,
  deletionData,
  deletionType,
  classname,
  verifyPhrase = "yesdelete",
}) => {
  const [verifyText, setVerifyText] = useState<string>("");
  const [showError, setShowError] = useState<boolean>(false);
  const [debouncing, setDebouncing] = useState<any>(null);
  const enableDelete = verifyText !== "" && verifyText === verifyPhrase;
  useEffect(() => {
    // User typed something, restart the debounce timer
    if (debouncing) {
      clearTimeout(debouncing);
      setDebouncing(null);
    }
    if (verifyText !== "") {
      // Remove error while the user is typing
      setShowError(false);
      const timer = setTimeout(() => {
        // Debounce finished, reset to null and check what they've typed so far
        setDebouncing(null);
        if (verifyText !== "" && verifyText !== verifyPhrase) {
          setShowError(true);
        } else {
          setShowError(false);
        }
      }, 500);
      setDebouncing(timer);
    }
    // Clean up existing timeout on unmount
    return () => {
      if (debouncing) {
        clearTimeout(debouncing);
      }
    };
  }, [verifyText]);

  function renderUserSubtitle() {
    const multiple = (deletionData.teamMembers ?? 0) > 1;
    return (
      <Typography className={classes.warningSubHeader} variant="subtitle1">
        {`${multiple ? "These" : "This"} team member${
          multiple ? "s" : ""
        } will no longer have access to Suralink`}
      </Typography>
    );
  }

  function renderDepartmentSubtitle() {
    let deletions: string[] = [];
    if (deletionData.teamMembers) {
      deletions.push(
        `${deletionData.teamMembers} Team Member${
          deletionData.teamMembers > 1 ? "s" : ""
        }`
      );
    }
    if (deletionData.invitations) {
      deletions.push(
        `${deletionData.invitations} invitation${
          deletionData.invitations > 1 ? "s" : ""
        }`
      );
    }
    if (deletionData.clients) {
      deletions.push(
        `${deletionData.clients} client${deletionData.clients > 1 ? "s" : ""}`
      );
    }

    const thisOrThese =
      (deletionData.teamMembers ?? 0) + (deletionData.invitations ?? 0) > 1 ||
      (deletionData.clients ?? 0) > 1
        ? "These"
        : "This";
    const teamNoAccess =
      deletionData.teamMembers || deletionData.invitations
        ? ` user${
            (deletionData.teamMembers ?? 0) + (deletionData.invitations ?? 0) >
            1
              ? "s"
              : ""
          }`
        : "";
    const clientNoAccess = deletionData.clients
      ? ` ${
          (deletionData.teamMembers || deletionData.invitations) &&
          deletionData.clients === 1
            ? " this "
            : ""
        }client${deletionData.clients > 1 ? "s" : ""}`
      : "";
    const teamAndClientNoAccess =
      (deletionData.teamMembers || deletionData.invitations) &&
      deletionData.clients
        ? " and"
        : "";
    let deletionText = "";
    if (deletions.length > 2) {
      const commaDeletions = deletions.splice(0, deletions.length - 1);
      deletionText = `${commaDeletions.join(", ")}${
        deletions.length === 1 ? ` and ${deletions[0]}` : ""
      }`;
    } else if (deletions.length > 1) {
      deletionText = `${deletions.join(" and ")}`;
    } else if (deletions.length > 0) {
      deletionText = `${deletions[0]}`;
    }
    return (
      <Typography className={classes.warningSubHeader} variant="subtitle1">
        {`You are deleting `}
        <Typography
          className={classes.boldPhrase}
          variant="subtitle1"
          component="span"
        >
          {deletionText}.
        </Typography>
        {` ${thisOrThese}${teamNoAccess}${teamAndClientNoAccess}${clientNoAccess} will no longer have access to Suralink.`}
      </Typography>
    );
  }

  function getSubtitle() {
    switch (deletionType) {
      case "user":
        return renderUserSubtitle();
      case "department":
        return renderDepartmentSubtitle();
    }
  }

  function renderUserAbsolutelySure() {
    return (
      <Typography
        className={classes.absolutelySureText}
        variant="subtitle1"
        component="span"
      >
        {`If you are absolutely sure about deleting this user, type `}
        <Typography
          className={classes.boldPhrase}
          variant="subtitle1"
          component="span"
        >
          {`"${verifyPhrase}"`}
        </Typography>
        {` in the box below and then submit the form again:`}
      </Typography>
    );
  }

  function renderDepartmentAbsolutelySure() {
    return (
      <Typography
        className={classes.absolutelySureText}
        variant="subtitle1"
        component="span"
      >
        {`If you are absolutely sure about deleting this department, type `}
        <Typography
          className={classes.boldPhrase}
          variant="subtitle1"
          component="span"
        >
          {`"${verifyPhrase}"`}
        </Typography>
        {` in the box below and then submit the form again:`}
      </Typography>
    );
  }

  function getAbsolutelySureMessage() {
    switch (deletionType) {
      case "user":
        return renderUserAbsolutelySure();
      case "department":
        return renderDepartmentAbsolutelySure();
    }
  }

  function renderUserHeader() {
    const userCount =
      (deletionData.teamMembers ?? 0) + (deletionData.invitations ?? 0);
    return (
      <Typography className={classes.warningHeader} variant="h4">
        {`You are deleting ${userCount > 1 ? userCount : "a"} Team Member${
          userCount > 1 ? "s" : ""
        }. This action is permanent! `}
      </Typography>
    );
  }

  function renderDepartmentHeader() {
    const userCount =
      (deletionData.teamMembers ?? 0) + (deletionData.invitations ?? 0);
    return (
      <Typography className={classes.warningHeader} variant="h4">
        {`This action is permanent! You are deleting the ${deletionData
          .department?.name ?? ""} department${
          userCount > 0
            ? ` and ${userCount} user${userCount > 1 ? "s" : ""}`
            : ""
        }.`}
      </Typography>
    );
  }

  function getHeader() {
    switch (deletionType) {
      case "user":
        return renderUserHeader();
      case "department":
        return renderDepartmentHeader();
    }
  }

  return (
    <div className={cx(classes.deleteContainer, classname)}>
      <div className={classes.topPortion}>
        {getHeader()}
        {getSubtitle()}
        {getAbsolutelySureMessage()}
        <div className={classes.verifyDeleteInputContainer}>
          <TextField
            variant="outlined"
            value={verifyText}
            onChange={(e) => {
              setVerifyText(e.target.value);
            }}
            error={showError}
            helperText={showError ? "Please type the input correctly" : ""}
            inputProps={{
              className: cx(classes.verificationInput),
            }}
            FormHelperTextProps={{
              className: cx(classes.inputErrorMessage),
            }}
          />
        </div>
      </div>

      <hr />
      <div className={classes.buttons}>
        <Button
          id={`btn-yes-delete-cancel`}
          className={cx(classes.button, classes.cancelButton)}
          variant="outlined"
          onClick={onCancelDelete}
        >
          Cancel
        </Button>
        <Button
          id={`btn-yes-delete-confirm`}
          variant="contained"
          color="secondary"
          className={cx(classes.button, classes.confirmButton, {
            [classes.disabled]: !enableDelete,
          })}
          onClick={onConfirmDelete}
          disabled={!enableDelete}
        >
          Delete
        </Button>
      </div>
    </div>
  );
};

export default YesDelete;
